/* eslint-disable @typescript-eslint/no-explicit-any */
import ensureObject from '../shared/ensureObject';
import { debounce } from '../Utils';

import { FileDesc } from '../../types/shared/FileDesc';

type ResponseHandler = (response: unknown) => void;
type Message = { action: string; args?: Array<unknown> };

const DEBOUNCE_DELAY_MS = 40;
const RESPONSE_TIMEOUT = 5000;

let responseHandlers: Map<string, ResponseHandler> = new Map();

window.addEventListener('message', (event) => {
  let response = ensureObject(event.data);
  if (response && typeof response.id === 'string') {
    let responseHandler = responseHandlers.get(response.id);
    if (responseHandler) {
      responseHandlers.delete(response.id);
      responseHandler(response.data);
    }
  }
});

function sendMessage(message: Message): Promise<unknown> {
  if (window.parent === window) {
    return Promise.resolve();
  }
  let id = generateId();
  return new Promise((resolve, reject) => {
    let timeout: TimeoutID | null | undefined;
    let onResponse = (data: any) => {
      if (timeout != null) {
        clearTimeout(timeout);
      }
      resolve(data);
    };
    responseHandlers.set(id, onResponse);
    window.parent.postMessage({ id, ...message }, '*');
    timeout = setTimeout(() => {
      if (responseHandlers.has(id)) {
        responseHandlers.delete(id);
        const errorMessage = JSON.stringify(message);
        reject(
          new Error(
            t('Timeout waiting for response for message: {errorMessage}', {
              errorMessage,
            }),
          ),
        );
      }
    }, RESPONSE_TIMEOUT);
  });
}

function onResize(width: number, height: number) {
  // Notify the parent window that our height has changed
  return sendMessage({
    action: 'setFrameSize',
    args: [width, height],
  });
}

function scrollToFrameTop() {
  return sendMessage({
    action: 'scrollToFrameTop',
  });
}

function onAttachmentClick(file: FileDesc) {
  return sendMessage({
    action: 'previewAttachment',
    args: [file],
  });
}

function onFullScreenToggle(isFullScreen: boolean) {
  return sendMessage({
    action: 'toggleFullScreen',
    args: [isFullScreen],
  });
}

function onClose() {
  return sendMessage({
    action: 'close',
  });
}

function showProfile(vendorID: string) {
  return sendMessage({
    action: 'showProfile',
    args: [vendorID],
  });
}

let generateId = (() => {
  let nextInt = 0;
  let getRandomPart = () => {
    let len = 5;
    let base = 36;
    let min = base ** (len - 1);
    let max = base ** len;
    let num = Math.floor(Math.random() * (max - min)) + min;
    return num.toString(base);
  };
  return () => {
    let count = nextInt++;
    return getRandomPart() + count.toString(36);
  };
})();

let CrossFrameMessenger = {
  onResize: debounce(onResize, DEBOUNCE_DELAY_MS),
  onAttachmentClick,
  onFullScreenToggle,
  onClose,
  scrollToFrameTop,
  showProfile,
};

export default CrossFrameMessenger;
